import { HttpErrorResponse } from '@angular/common/http';

export type SettingsErrorKind =
  | 'validation'
  | 'unauthorized'
  | 'forbidden'
  | 'not-found'
  | 'conflict'
  | 'rate-limit'
  | 'timeout'
  | 'offline'
  | 'server'
  | 'unexpected';

export class SettingsApiError extends Error {
  constructor(
    readonly kind: SettingsErrorKind,
    readonly status: number,
    readonly retryAfterSeconds?: number,
    message?: string,
  ) {
    super(message ?? settingsErrorMessage(kind, retryAfterSeconds));
    this.name = 'SettingsApiError';
  }
}

export function mapSettingsError(error: unknown): SettingsApiError {
  if (error instanceof SettingsApiError) {
    return error;
  }

  if (error instanceof HttpErrorResponse) {
    const message = readSettingsApiMessage(error.error);
    if (error.status === 0) {
      return new SettingsApiError(
        typeof navigator !== 'undefined' && !navigator.onLine
          ? 'offline'
          : 'unexpected',
        error.status,
        undefined,
        message,
      );
    }
    if (error.status === 400) {
      return new SettingsApiError('validation', 400, undefined, message);
    }
    if (error.status === 401) {
      return new SettingsApiError('unauthorized', 401, undefined, message);
    }
    if (error.status === 403) {
      return new SettingsApiError('forbidden', 403, undefined, message);
    }
    if (error.status === 404) {
      return new SettingsApiError('not-found', 404, undefined, message);
    }
    if (error.status === 409) {
      return new SettingsApiError('conflict', 409, undefined, message);
    }
    if (error.status === 429) {
      const retryAfter = Number(error.headers.get('Retry-After'));
      return new SettingsApiError(
        'rate-limit',
        429,
        Number.isFinite(retryAfter) ? retryAfter : undefined,
        message,
      );
    }
    if (error.status >= 500) {
      return new SettingsApiError('server', error.status, undefined, message);
    }
    if (message) {
      return new SettingsApiError('unexpected', error.status, undefined, message);
    }
  }

  if (error instanceof Error && error.name === 'TimeoutError') {
    return new SettingsApiError('timeout', 0);
  }

  return new SettingsApiError('unexpected', 0);
}

function readSettingsApiMessage(body: unknown): string | undefined {
  if (!body || typeof body !== 'object') return undefined;
  const record = body as Record<string, unknown>;
  const business = typeof record['error'] === 'string' ? record['error'].trim() : '';
  const details = detailMessages(record['details']).filter(item => item !== business);
  if (business && details.length) {
    return [business.replace(/\.$/, ''), ...details].join('. ');
  }
  if (business) return business;
  if (details.length) return details.join('. ');
  return undefined;
}

function detailMessages(details: unknown): string[] {
  if (!details || typeof details !== 'object' || Array.isArray(details)) return [];
  const messages: string[] = [];
  for (const value of Object.values(details as Record<string, unknown>)) {
    if (typeof value === 'string' && value.trim()) messages.push(value.trim());
    if (!Array.isArray(value)) continue;
    for (const item of value) {
      if (typeof item === 'string' && item.trim()) messages.push(item.trim());
    }
  }
  return messages;
}

function settingsErrorMessage(
  kind: SettingsErrorKind,
  retryAfterSeconds?: number,
): string {
  switch (kind) {
    case 'validation':
      return 'Revisa los datos del formulario e intenta de nuevo.';
    case 'unauthorized':
      return 'Tu sesión expiró. Vuelve a iniciar sesión.';
    case 'forbidden':
      return 'No tienes permiso para realizar esta acción.';
    case 'not-found':
      return 'El registro no existe o ya no está disponible.';
    case 'conflict':
      return 'Ya existe un registro con estos datos.';
    case 'rate-limit':
      return retryAfterSeconds
        ? `Demasiadas solicitudes. Intenta de nuevo en ${retryAfterSeconds} segundos.`
        : 'Demasiadas solicitudes. Espera un momento antes de volver a intentar.';
    case 'timeout':
      return 'La solicitud tardó demasiado. Verifica tu conexión e intenta de nuevo.';
    case 'offline':
      return 'No hay conexión a internet. Verifica tu red e intenta de nuevo.';
    case 'server':
    case 'unexpected':
      return 'No fue posible completar la solicitud. Intenta de nuevo más tarde.';
  }
}
